"use client";

import { useState } from "react";
import { SectionGraphic } from "./SectionGraphic";

interface FAQ {
  question: string;
  answer: string;
}

interface FAQAccordionProps {
  faqs: FAQ[];
  /** Optional array of SectionGraphic IDs (same length as faqs). null = no graphic. */
  faqGraphics?: (string | null)[];
}

export function FAQAccordion({ faqs, faqGraphics = [] }: FAQAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="divide-y divide-[hsl(var(--border))]">
      {faqs.map((faq, i) => (
        <FAQItem
          key={i}
          faq={faq}
          isOpen={openIndex === i}
          onToggle={() => setOpenIndex(openIndex === i ? null : i)}
          graphicId={faqGraphics[i] ?? null}
        />
      ))}
    </div>
  );
}

interface FAQItemProps {
  faq: FAQ;
  isOpen: boolean;
  onToggle: () => void;
  graphicId: string | null;
}

function FAQItem({ faq, isOpen, onToggle, graphicId }: FAQItemProps) {
  return (
    <div className="py-5">
      <button
        onClick={onToggle}
        className="flex items-start justify-between gap-4 w-full text-left cursor-pointer"
        aria-expanded={isOpen}
      >
        <span className="font-semibold text-foreground text-base leading-snug pr-4">
          {faq.question}
        </span>
        <span
          className={`shrink-0 w-6 h-6 rounded-full border border-[hsl(var(--border))] flex items-center justify-center text-primary text-lg transition-transform duration-200 mt-0.5 ${
            isOpen ? "rotate-45" : ""
          }`}
        >
          +
        </span>
      </button>

      {/* CSS grid trick: 0fr ↔ 1fr for smooth height animation */}
      <div
        className="grid transition-[grid-template-rows] duration-[260ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <p className="mt-4 text-muted-foreground leading-relaxed text-base pr-10">
            {faq.answer}
          </p>
          {graphicId && (
            <div
              className={`mt-4 transition-opacity duration-300 ${
                isOpen ? "opacity-100" : "opacity-0"
              }`}
            >
              <SectionGraphic id={graphicId} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
